// ─── renderResultEntry ───────────────────────────────────────────────────
// Pure dispatch by entry.results.contentFormat | platform → per-platform renderer.
// Used by Step3History (the History wizard step in EscContentCreatorPro) and by
// RunDetailPage (the SA/TA run inspector).

import React from 'react';
import CarouselResultEntry from './CarouselResultEntry.jsx';
import SeoResultEntry from './SeoResultEntry.jsx';
import NewsletterResultEntry from './NewsletterResultEntry.jsx';
import InstagramResultEntry from './InstagramResultEntry.jsx';
import FacebookResultEntry from './FacebookResultEntry.jsx';
import YouTubeResultEntry from './YouTubeResultEntry.jsx';
import PinterestResultEntry from './PinterestResultEntry.jsx';
import LandingResultEntry from './LandingResultEntry.jsx';

export default function renderResultEntry(entry, rendererProps) {
  const { t, w: _w } = rendererProps; // eslint-disable-line no-unused-vars
  const r = entry.results || {};
  const c = r.content;
  const color = entry.modeConfig?.color || '#6366F1';

  if (r.status === 'cancelled' || r.cancelled) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '16px 18px', borderRadius: '10px',
        background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.25)',
        color: '#F59E0B', fontSize: '14px', fontWeight: 600,
      }}>
        <span>⊘ Generazione annullata dall'utente — nessun contenuto prodotto.</span>
      </div>
    );
  }

  // Carousel: use dedicated slide renderer
  if (r.contentFormat === 'carousel' && c?.slides) {
    return <CarouselResultEntry entry={entry} w={_w} t={t} />;
  }

  // Blog SEO: dedicated structured renderer
  if (r.contentFormat === 'seo') {
    return (
      <SeoResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // LinkedIn Newsletter: dedicated structured renderer
  if (r.contentFormat === 'newsletter') {
    return (
      <NewsletterResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // Instagram post: platform-specific renderer (dispatch by platform because
  // content_format='post' is shared with linkedin-post and facebook-post).
  if (r.platform === 'instagram') {
    return (
      <InstagramResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // Facebook post: platform-specific renderer (shares content_format='post'
  // with linkedin- and instagram-post; platform is the discriminator).
  if (r.platform === 'facebook') {
    return (
      <FacebookResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // YouTube long-form: platform-specific renderer (content shape per
  // docs/frontend/youtube-post-rendering.md — title, title_variants,
  // description_hook, description, chapters, tags, thumbnail_concept,
  // cta_script, seo_keywords, hashtags).
  if (r.platform === 'youtube') {
    return (
      <YouTubeResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // Pinterest post: platform-specific renderer (Path B — own typed model;
  // content shape per docs/frontend/pinterest-post-rendering.md —
  // pin_title, pin_description, board_suggestion, hashtags, cta_url).
  if (r.platform === 'pinterest') {
    return (
      <PinterestResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // Landing page: dedicated structured renderer for content_format='landing'.
  // Consumes the 7-block shape (HERO/PROBLEM/SOLUTION/FEATURES/SOCIAL_PROOF/
  // FAQ/FINAL_CTA) with anchor IDs and inline-HTML sanitization. Warning
  // surfacing is handled by the outer chrome (Step3History.jsx WarningBanner).
  if (r.contentFormat === 'landing') {
    return (
      <LandingResultEntry
        entry={entry}
        copiedId={rendererProps.copiedId}
        onCopy={rendererProps.onCopy}
        w={_w}
        t={t}
      />
    );
  }

  // Structured content object: { hook, body, cta, hashtags, full_text }
  if (c && typeof c === 'object') {
    return (
      <div>
        {c.hook && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: t.textMuted || '#7a7a8e', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>HOOK</label>
            <div style={{ padding: '12px 16px', background: t.inputBg, borderRadius: '8px', borderLeft: `3px solid ${color}`, fontWeight: 600, fontSize: '15px', lineHeight: 1.5, color: t.text }}>{c.hook}</div>
          </div>
        )}
        {c.body && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: t.textMuted || '#7a7a8e', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>CONTENUTO</label>
            <div style={{ padding: '14px 16px', background: t.inputBg, borderRadius: '8px', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap', color: t.text }}>{c.body}</div>
          </div>
        )}
        {c.cta && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: t.textMuted || '#7a7a8e', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>CTA</label>
            <div style={{ padding: '10px 16px', background: `${color}08`, borderRadius: '8px', border: `1px solid ${color}20`, fontSize: '13px', lineHeight: 1.5, color: t.text, fontStyle: 'italic' }}>{c.cta}</div>
          </div>
        )}
        {c.hashtags?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {c.hashtags.map((tag, i) => (
              <span key={i} style={{ padding: '3px 10px', borderRadius: '20px', background: `${color}15`, color, fontSize: '12px', fontWeight: 500 }}>
                {tag.startsWith('#') ? tag : `#${tag}`}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Legacy: content is a markdown string
  if (c && typeof c === 'string') {
    return (
      <div>
        <pre style={{ fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: t.text, background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: '10px', padding: '16px', margin: 0, fontFamily: "'DM Sans', sans-serif" }}>{c}</pre>
      </div>
    );
  }

  // Fallback: raw JSON
  return <pre style={{ fontSize: '11px', overflowX: 'auto', color: t.text }}>{JSON.stringify(r, null, 2)}</pre>;
}
